import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { enderecoDeArquivo } from '@/shared/api/client';
import { policies, type InsuredDetail, type Policy } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCivilDate, formatDate, formatRate } from '@/shared/lib/format';
import { Button } from '@/shared/ui/Button';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Pagination } from '@/shared/ui/Pagination';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';
import { buttonClassName } from '@/shared/ui/buttonStyles';

import { CancellationRequestDialog } from '../aftercare/PolicyRequests';
import { numeroDaApolice, simNao, tomadorDaApolice } from '../portfolio/apolice';
import {
  avisoDoModeloDoLaudo,
  motivoSemLaudo,
  PERMISSAO_DO_LAUDO,
} from '../portfolio/laudo';

import { AVISO_CANCELAMENTO, PENDENCIA_VIGENCIA } from './rotulos';

const limite = 20;

/** Permissao do pedido de cancelamento que o SeguraCore ja tem. */
const PERMISSAO_CANCELAR = 'cancelamentos.cancelamento.create';

/**
 * Aba "Apolices" do segurado (P-SEG-2), com as colunas do OnPoint na mesma ordem:
 * Numero, Modalidade, Contem endosso?, Tomador, Status, Emissao, Vencimento, Vigencia
 * e o menu da linha (Visualizar apolice, Visualizar laudo de emissao e Cancelar
 * apolice manualmente).
 *
 * A leitura e a da carteira, pelo segurado (`GET /api/v1/policies?insured_id=`), com
 * a permissao e o escopo de corretora dela. A Vigencia e regra sem fonte (SEG-037) e
 * fica vazia; o cancelamento e o pedido com aprovacao que ja existe, sem regra
 * financeira nova.
 *
 * "Visualizar laudo de emissao" (SEG-039) baixa o laudo da apolice
 * (`GET /api/v1/policies/{policyId}/issuance-report`), montado pelo modelo da empresa
 * ou pelo neutro do sistema (P-MIN-3). Como no OnPoint, so aparece para quem tem
 * `apolices.laudo.view`, que nao vale para a corretora; a apolice de antes do laudo
 * mostra o item desligado, com o motivo.
 */
export function InsuredPoliciesTab({ segurado }: { segurado: InsuredDetail }) {
  const { can } = useAuth();
  const podeVer = can('apolices.apolice.view');
  const podeCancelar = can(PERMISSAO_CANCELAR);
  const podeVerLaudo = can(PERMISSAO_DO_LAUDO);
  const toast = useToast();
  const [offset, setOffset] = useState(0);
  const [cancelando, setCancelando] = useState<Policy | null>(null);

  const laudo = useMutation({
    mutationFn: (apolice: Policy) => policies.issuanceReport(apolice.id),
    onSuccess: (link) => {
      window.open(enderecoDeArquivo(link.url), '_blank', 'noopener,noreferrer');
      const aviso = avisoDoModeloDoLaudo(link);
      if (aviso) toast.info(aviso);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const consulta = useQuery({
    queryKey: ['policies', 'por-segurado', segurado.id, offset],
    queryFn: ({ signal }) =>
      policies.list(
        { insured_id: segurado.id, issuer_kind: 'seguradora', limit: limite, offset },
        signal,
      ),
    enabled: podeVer,
  });

  if (!podeVer) {
    return (
      <DetailCard title="Apolices">
        <EmptyState
          title="Consulta restrita"
          description="As apolices do segurado exigem a leitura da carteira de apolices."
        />
      </DetailCard>
    );
  }

  const colunas: Column<Policy>[] = [
    {
      key: 'numero',
      header: 'Numero',
      render: (apolice) => (
        <span className="whitespace-nowrap font-medium tabular-nums text-ink-900">
          {numeroDaApolice(apolice)}
        </span>
      ),
    },
    {
      key: 'modalidade',
      header: 'Modalidade',
      render: (apolice) => apolice.modality.label,
    },
    {
      key: 'endosso',
      header: 'Contem endosso?',
      hideBelow: 'md',
      render: (apolice) => simNao((apolice.endorsement_count ?? 0) > 0),
    },
    {
      key: 'tomador',
      header: 'Tomador',
      render: (apolice) => <p className="truncate">{tomadorDaApolice(apolice).nome}</p>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (apolice) => <StatusBadge kind="apolice" value={apolice.status} />,
    },
    {
      key: 'emissao',
      header: 'Emissao',
      hideBelow: 'sm',
      render: (apolice) => formatDate(apolice.issued_at),
    },
    {
      key: 'vencimento',
      header: 'Vencimento',
      hideBelow: 'sm',
      render: (apolice) => formatCivilDate(apolice.coverage_end),
    },
    {
      key: 'vigencia',
      header: 'Vigencia',
      hideBelow: 'md',
      render: (apolice) =>
        apolice.elapsed_bps == null ? (
          <span className="text-ink-400" title={PENDENCIA_VIGENCIA}>
            -
          </span>
        ) : (
          formatRate(apolice.elapsed_bps)
        ),
    },
    {
      key: 'acoes',
      header: 'Acoes',
      render: (apolice) => {
        const cancelavel = podeCancelar && apolice.status !== 'cancelada';
        return (
          <div
            className="flex flex-wrap gap-1"
            onClick={(evento) => evento.stopPropagation()}
          >
            <Link
              to={`/apolices/${apolice.id}`}
              className={buttonClassName({ variant: 'secondary', size: 'sm' })}
            >
              Visualizar apolice
            </Link>
            {podeVerLaudo ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={motivoSemLaudo(apolice) !== undefined}
                title={
                  motivoSemLaudo(apolice) ?? 'Baixa o laudo de emissao desta apolice.'
                }
                loading={laudo.isPending && laudo.variables?.id === apolice.id}
                onClick={() => laudo.mutate(apolice)}
              >
                Visualizar laudo de emissao
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              disabled={!cancelavel}
              title={
                cancelavel
                  ? AVISO_CANCELAMENTO
                  : apolice.status === 'cancelada'
                    ? 'A apolice ja esta cancelada.'
                    : 'O pedido de cancelamento exige a permissao de solicitar cancelamento.'
              }
              onClick={() => (cancelavel ? setCancelando(apolice) : undefined)}
            >
              Cancelar apolice manualmente
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <DetailCard
      title="Apolices"
      description="Apolices emitidas para este segurado, como a apolice gravou na emissao."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <div className="flex flex-col gap-3">
        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(apolice) => apolice.id}
          empty={<EmptyState icon="file" title="Nenhuma apolice para este segurado" />}
        />
        {consulta.data ? (
          <Pagination
            total={consulta.data.total}
            limit={limite}
            offset={offset}
            onChange={setOffset}
          />
        ) : null}
        <p className="text-2xs text-ink-500">
          Vigencia: {PENDENCIA_VIGENCIA} Cancelar apolice manualmente:{' '}
          {AVISO_CANCELAMENTO}
        </p>
      </div>
      <CancellationRequestDialog
        policy={cancelando}
        open={cancelando !== null}
        onClose={() => setCancelando(null)}
      />
    </DetailCard>
  );
}
